import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { useToast } from '@/components/common/toast';
import { FilterBar } from '@/components/common/FilterBar';
import { AsyncButton } from '@/components/common/AsyncButton';
import { Select2 } from '@/components/forms/Select2';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { formatNumber } from '@/utils/format';
import { ApiError } from '@/api/client/abpError';
import { revShareResultsApi } from '@/api/services/revenueShare';
import { MONTH_OPTIONS, type RevShareResultRowDto } from '@/api/types/revenueShare';
import { useEnum } from '@/features/common/enums/useEnumOptions';

function currentYear() {
  return new Date().getFullYear();
}
function currentMonth() {
  return new Date().getMonth() + 1;
}
function currentQuarter() {
  return Math.ceil(currentMonth() / 3);
}

export function RevShareResultsPage() {
  const toast = useToast();
  const [profileId, setProfileId] = useState('');
  const [year, setYear] = useState(String(currentYear()));
  const [month, setMonth] = useState('');
  const [quarter, setQuarter] = useState('');
  const [rows, setRows] = useState<RevShareResultRowDto[]>([]);
  const lastWarnRef = useRef('');

  const quarterOptions = useEnum('RevenueShareQuarter');
  const profiles = useQuery({ queryKey: ['rev-share', 'profiles'], queryFn: revShareResultsApi.getProfiles, staleTime: 5 * 60_000 });
  const years = useQuery({ queryKey: ['rev-share', 'years'], queryFn: revShareResultsApi.getYears, staleTime: 5 * 60_000 });

  const profile = profiles.data?.find((p) => String(p.id) === profileId);
  const isQuarterly = profile?.periodType === 'Quarterly';
  const isMonthly = profile?.periodType === 'Monthly';

  const onProfileChange = (v: string | null) => {
    const id = v ?? '';
    setProfileId(id);
    const p = profiles.data?.find((x) => String(x.id) === id);
    setYear(String(currentYear()));
    if (p?.periodType === 'Monthly') {
      setMonth(String(currentMonth()));
      setQuarter('');
    } else if (p?.periodType === 'Quarterly') {
      setQuarter(String(currentQuarter()));
      setMonth('');
    } else {
      setMonth('');
      setQuarter('');
    }
  };

  // Year/month guard: in the current year, future months are disabled.
  const monthOptions = useMemo(() => {
    const cy = currentYear();
    const cm = currentMonth();
    const guard = Number(year) === cy;
    return MONTH_OPTIONS.map((o) => ({ ...o, disabled: guard && o.value > cm }));
  }, [year]);

  const isFuturePeriod = () => {
    const y = Number(year);
    const cy = currentYear();
    if (y > cy) return true;
    if (y < cy) return false;
    if (quarter) return Number(quarter) > currentQuarter();
    if (month) return Number(month) > currentMonth();
    return false;
  };

  const fetch = async () => {
    if (!profileId) { toast.error('Profile field is mandatory'); return; }
    if (!year) { toast.error('Year field is mandatory'); return; }
    if (!month && !quarter) { toast.error('Period field is mandatory'); return; }

    try {
      const res = await revShareResultsApi.getReport({
        id: Number(profileId),
        year: Number(year),
        month: isQuarterly ? null : (month ? Number(month) : null),
        quarter: isQuarterly ? (quarter ? Number(quarter) : null) : null,
      });
      setRows(res.items ?? []);
      if (res.message && res.message !== lastWarnRef.current) {
        lastWarnRef.current = res.message;
        toast.warning(res.message);
      }
    } catch (e) {
      setRows([]);
      if (!isFuturePeriod()) {
        toast.warning(e instanceof ApiError ? e.message : 'Something went wrong while processing your request.');
      }
    }
  };

  const fields: React.ReactNode[] = [
    <Select2 key="profile" label="Profiles" allowClear placeholder="N/A" value={profileId} options={(profiles.data ?? []).map((p) => ({ value: p.id, label: p.text }))} onChange={onProfileChange} />,
    <Select2 key="year" label="Year" searchable={false} allowClear={false} value={year} options={(years.data ?? []).map((y) => ({ value: y, label: String(y) }))} onChange={(v) => setYear(v ?? String(currentYear()))} />,
    ...(isMonthly ? [<Select2 key="month" label="Month" searchable={false} allowClear={false} value={month} options={monthOptions} onChange={(v) => setMonth(v ?? '')} />] : []),
    ...(isQuarterly ? [<Select2 key="quarter" label="Quarter" searchable={false} allowClear={false} value={quarter} options={quarterOptions} onChange={(v) => setQuarter(v ?? '')} />] : []),
    <div key="hwm">
      <label className="form-label">HWM</label>
      <input className="form-control" value={profile ? profile.highWaterMark : ''} disabled />
    </div>,
    <div key="pct">
      <label className="form-label">RevShare %</label>
      <input className="form-control" value={profile ? profile.revSharePercentage : ''} disabled />
    </div>,
  ];

  const num = (v: number | null) => formatNumber(v, 2);

  return (
    <div className="rev-share-results-page">
      <PageHeader title="Rev Share Results" breadcrumb={['Home', 'Rev Share Results']} />

      <FilterBar
        fields={fields}
        actions={<AsyncButton className="btn btn-primary btn-sm" icon="fa fa-search" onClick={fetch}>Fetch</AsyncButton>}
      />

      <DataGrid
        columns={[
          { key: 'hwmPeriod', title: 'HWM Period', className: 'text-start', value: (r) => r.hwmPeriodSort, cell: (r) => r.hwmPeriod || 'N/A' },
          { key: 'pnl', title: 'PnL', className: 'text-end', value: (r) => r.pnl, cell: (r) => num(r.pnl) },
          { key: 'swaps', title: 'Swaps', className: 'text-end', value: (r) => r.swaps, cell: (r) => num(r.swaps) },
          { key: 'commissions', title: 'Commissions', className: 'text-end', value: (r) => r.commissions, cell: (r) => num(r.commissions) },
          { key: 'netPnL', title: 'Net PnL', className: 'text-end', value: (r) => r.netPnL, cell: (r) => num(r.netPnL) },
          { key: 'hwm', title: 'HWM', className: 'text-end', value: (r) => r.hwm, cell: (r) => num(r.hwm) },
          { key: 'rsPnL', title: 'RS PnL', className: 'text-end', value: (r) => r.rsPnL, cell: (r) => num(r.rsPnL) },
          { key: 'revShare', title: 'Rev Share', className: 'text-end', value: (r) => r.revShare, cell: (r) => num(r.revShare) },
          { key: 'equity', title: 'Equity', className: 'text-end', value: (r) => r.equity, cell: (r) => num(r.equity) },
          { key: 'newEquity', title: 'New Equity', className: 'text-end', value: (r) => r.newEquity, cell: (r) => num(r.newEquity) },
          { key: 'credit', title: 'Credit', className: 'text-end', value: (r) => r.credit, cell: (r) => num(r.credit) },
        ] as DataGridColumn<RevShareResultRowDto>[]}
        data={rows}
        rowKey={(r) => `${r.hwmPeriod}-${r.hwmPeriodSort}`}
        card
        includeExcel
        excelFileName="Rev Share Results"
        emptyMessage="No data."
      />
    </div>
  );
}
